// ─────────────────────────────────────────────────────────────────────────────
//  services/bridge.js — Maps legacy app data shapes to command payloads
//
//  The existing app uses Date.now() integer IDs, product names as identifiers,
//  and flat JSONB objects. The commands expect UUIDs and normalized structures.
//  This module bridges the gap during the Phase 8 transition.
//
//  During transition, foreign keys (product_id, customer_id, payment_account_id)
//  are passed as null — the DB commands accept this (_assert_live returns early
//  for null). The product_name/description string fields carry the identity.
// ─────────────────────────────────────────────────────────────────────────────
import { createSale, createSaleDraft, postSale } from './commands';
import { idempotencyKey } from './rpc';

// ── Helpers ────────────────────────────────────────────────────────────────
const isUUID = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
/** Return the relational UUID for a record, or null. */
const resolveRid = (record) => record?._rid || (isUUID(record?.id) ? record.id : null);

/**
 * Resolve a local payment entry to its relational payment_account_id + wallet_type.
 *
 * @param {object} payment - { type, accountKey, amount, ... }
 * @param {Array}  paymentAccounts - local state array with _rid stamps
 * @returns {{ payment_account_id: string|null, wallet_type: string }}
 */
function resolvePaymentMapping(payment, paymentAccounts) {
  const pType = payment.type || 'cash';
  const accountKey = payment.accountKey || pType;

  // Credit = deferred obligation, no wallet
  if (pType === 'credit' || accountKey === 'credit') {
    return { payment_account_id: null, wallet_type: 'credit' };
  }

  const accounts = paymentAccounts || [];
  let account = null;

  if (accountKey.includes('|')) {
    // Composite key: 'bank|123456789' or 'mobilemoney|123456789'
    const localId = accountKey.split('|')[1];
    account = accounts.find(a => String(a.id) === String(localId));
  } else if (accountKey === 'cash') {
    account = accounts.find(a => a.type === 'cash');
  } else {
    // Bare 'bank' or 'mobilemoney' (legacy, no sub-account)
    account = accounts.find(a => a.type === accountKey);
  }

  const rid = account ? resolveRid(account) : null;

  if (rid) {
    return { payment_account_id: rid, wallet_type: account.type };
  }

  // Graceful degradation: no _rid yet → credit fallback (DB CHECK allows it)
  return { payment_account_id: null, wallet_type: 'credit' };
}

// ── Payload builders ────────────────────────────────────────────────────────

/**
 * Build the sale header from the legacy updatedSaleData shape.
 */
function buildSaleHeader(data) {
  return {
    customer_name: data.customerName || null,
    customer_id: null, // TODO: resolve from DB when master data is synced
    sale_date: data.date || new Date().toISOString().split('T')[0],
    total_revenue: parseFloat(data.totalRevenue || 0),
    total_cost: parseFloat(data.totalCost || 0),
    profit: parseFloat(data.profit || 0),
    is_paid: !!data.isPaid,
  };
}

/**
 * Map legacy sale line items to the items array for commands.create_sale.
 */
function buildSaleItems(items) {
  return (items || []).map((it) => {
    const hasProductId = it.productId && /^[0-9a-f-]{36}$/i.test(it.productId);
    const hasServiceId = it.serviceId && /^[0-9a-f-]{36}$/i.test(it.serviceId);
    let itemType;
    if (it.type === 'service' && hasServiceId) itemType = 'SERVICE';
    else if (hasProductId) itemType = 'PRODUCT';
    else itemType = 'MANUAL';
    return {
      item_type: itemType,
      product_id: hasProductId ? it.productId : null,
      service_id: hasServiceId ? it.serviceId : null,
      product_name: it.productName || '',
      description: it.description || it.productName || 'Item',
      quantity: parseFloat(it.quantity || 0),
      unit_cost: parseFloat(it.cost || 0),
      selling_price: parseFloat(it.sellingPrice || 0),
    };
  });
}

/**
 * Build inventory movements from sale items.
 * Sales deduct stock: qty_delta is negative.
 */
function buildSaleMovements(items, saleDate) {
  return (items || [])
    .filter((it) => {
      if (it.type === 'service') return false;
      if (parseFloat(it.quantity || 0) <= 0) return false;
      // Only include movements for items with a valid product UUID
      const hasProductId = it.productId && /^[0-9a-f-]{36}$/i.test(it.productId);
      return hasProductId;
    })
    .map((it) => ({
      product_id: it.productId,
      qty_delta: -Math.abs(parseFloat(it.quantity || 0)),
      unit_cost: parseFloat(it.cost || 0),
      movement_value: -(Math.abs(parseFloat(it.quantity || 0)) * parseFloat(it.cost || 0)),
      accounting_date: saleDate || new Date().toISOString().split('T')[0],
    }));
}

/**
 * Map legacy payment objects to the payments array for commands.
 */
function buildSalePayments(payments, saleDate, paymentAccounts) {
  return (payments || [])
    .filter((p) => p.amount && parseFloat(p.amount) > 0)
    .map((p) => {
      const { payment_account_id, wallet_type } = resolvePaymentMapping(p, paymentAccounts);
      return {
        payment_account_id,
        wallet_type,
        amount: parseFloat(p.amount),
        payment_method: p.type || 'cash',
        payment_date: saleDate || new Date().toISOString().split('T')[0],
      };
    });
}

// ── High-level bridge functions ─────────────────────────────────────────────

/**
 * Persist a sale to the relational DB via commands.create_sale.
 * Returns { success, data } on success, throws on failure.
 *
 * @param {string} businessId - UUID from BusinessContext
 * @param {object} saleData   - The legacy updatedSaleData object from handleSaveSale
 * @param {string} token      - Supabase access token
 */
export async function persistSale(businessId, saleData, token, paymentAccounts) {
  if (!businessId || !token) return null; // silently skip if not ready

  const sale = buildSaleHeader(saleData);
  const items = buildSaleItems(saleData.items);
  const movements = buildSaleMovements(saleData.items, saleData.date);
  const payments = buildSalePayments(saleData.payments, saleData.date, paymentAccounts);

  return createSale(businessId, sale, items, movements, payments, token);
}

// ── PURCHASE BRIDGE ─────────────────────────────────────────────────────────

function buildPurchaseHeader(data) {
  return {
    supplier_id: null,
    supplier_name: data.supplier || null,
    purchase_date: data.date || new Date().toISOString().split('T')[0],
    total_cost: parseFloat(data.totalCost || 0),
    is_paid: !!data.isPaid,
  };
}

function buildPurchaseItems(items) {
  return (items || []).map((it) => {
    const qty = parseFloat(it.quantity || 0);
    const unitCost = parseFloat(it.unitPrice || it.costPrice || it.cost || 0);
    const lineCost = parseFloat(it.totalCost || 0) || (qty * unitCost);
    const hasProductId = it.productId && /^[0-9a-f-]{36}$/i.test(it.productId);
    return {
      item_type: hasProductId ? 'PRODUCT' : 'MANUAL',
      product_id: hasProductId ? it.productId : null,
      product_name: it.productName || '',
      description: it.description || it.productName || 'Item',
      quantity: qty,
      unit_cost: unitCost,
      line_cost: lineCost,
    };
  });
}

function buildPurchaseMovements(items, purchaseDate) {
  return (items || [])
    .filter((it) => {
      if (parseFloat(it.quantity || 0) <= 0) return false;
      const hasProductId = it.productId && /^[0-9a-f-]{36}$/i.test(it.productId);
      return hasProductId;
    })
    .map((it) => {
      const unitCost = parseFloat(it.unitPrice || it.costPrice || it.cost || 0);
      const qty = Math.abs(parseFloat(it.quantity || 0));
      return {
        product_id: it.productId,
        qty_delta: qty,
        unit_cost: unitCost,
        movement_value: qty * unitCost,
        accounting_date: purchaseDate || new Date().toISOString().split('T')[0],
      };
    });
}

function buildPurchasePayments(payments, purchaseDate, paymentAccounts) {
  return (payments || [])
    .filter((p) => p.amount && parseFloat(p.amount) > 0)
    .map((p) => {
      const { payment_account_id, wallet_type } = resolvePaymentMapping(p, paymentAccounts);
      return {
        payment_account_id,
        wallet_type,
        amount: parseFloat(p.amount),
        payment_method: p.type || 'cash',
        payment_date: purchaseDate || new Date().toISOString().split('T')[0],
      };
    });
}

/**
 * Persist a purchase to the relational DB.
 */
export async function persistPurchase(businessId, purchaseData, token, paymentAccounts) {
  if (!businessId || !token) return null;

  // Purchases use draft→post flow since the create_purchase command doesn't exist
  // (only create_purchase_draft + post_purchase). But for speed we import directly.
  const { rpc: rpcCall } = await import('./rpc');
  const items = buildPurchaseItems(purchaseData.items || [{ productName: purchaseData.productName, quantity: purchaseData.quantity, costPrice: purchaseData.costPrice }]);
  const movements = buildPurchaseMovements(purchaseData.items || [{ productName: purchaseData.productName, quantity: purchaseData.quantity, costPrice: purchaseData.costPrice }], purchaseData.date);
  const payments = buildPurchasePayments(purchaseData.payments, purchaseData.date, paymentAccounts);

  // Compute total_cost from items to guarantee it matches Σ line_cost (SQL validation)
  const purchase = buildPurchaseHeader(purchaseData);
  purchase.total_cost = items.reduce((sum, it) => sum + (it.line_cost || 0), 0);

  // Step 1: create draft
  const draftPayload = { business_id: businessId, purchase, items };
  const draftResult = await rpcCall('create_purchase_draft', idempotencyKey('pd'), draftPayload, token);

  const purchaseId = draftResult?.data?.purchase_id;
  if (!purchaseId) throw new Error('No purchase_id returned from draft');

  // Step 2: post (row_version=1 since we just created the draft)
  return rpcCall('post_purchase', idempotencyKey('pp'), {
    business_id: businessId,
    purchase_id: purchaseId,
    row_version: 1,
    movements,
    payments,
  }, token);
}

// ── EXPENSE BRIDGE ──────────────────────────────────────────────────────────

/**
 * Persist an expense to the relational DB.
 */
export async function persistExpense(businessId, expenseData, token, paymentAccounts) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');

  const expense = {
    category_id: null,
    category_name: expenseData.category || expenseData.expenseCategory || null,
    expense_date: expenseData.date || new Date().toISOString().split('T')[0],
    amount: parseFloat(expenseData.amount || 0),
    description: expenseData.description || expenseData.name || '',
    is_paid: !!expenseData.isPaid,
  };

  // Step 1: create draft
  const draftResult = await rpcCall('create_expense_draft', idempotencyKey('ed'), {
    business_id: businessId,
    expense,
  }, token);

  const expenseId = draftResult?.data?.expense_id;
  if (!expenseId) throw new Error('No expense_id returned from draft');

  // Step 2: post with payments array (row_version=1 since just created)
  const payments = [];
  if (expenseData.isPaid !== false) {
    // Map legacy payment entries or fall back to single payment
    if (Array.isArray(expenseData.payments) && expenseData.payments.length > 0) {
      expenseData.payments.forEach((p) => {
        if (p.amount && parseFloat(p.amount) > 0) {
          const mapped = resolvePaymentMapping(p, paymentAccounts);
          payments.push({
            payment_account_id: mapped.payment_account_id,
            wallet_type: mapped.wallet_type,
            amount: parseFloat(p.amount),
            payment_method: p.type || 'cash',
            payment_date: expenseData.date || new Date().toISOString().split('T')[0],
          });
        }
      });
    } else {
      const fallbackPayment = { type: expenseData.paymentMethod || 'cash', accountKey: expenseData.accountKey || expenseData.paymentMethod || 'cash' };
      const mapped = resolvePaymentMapping(fallbackPayment, paymentAccounts);
      payments.push({
        payment_account_id: mapped.payment_account_id,
        wallet_type: mapped.wallet_type,
        amount: parseFloat(expenseData.amount || 0),
        payment_method: expenseData.paymentMethod || 'cash',
        payment_date: expenseData.date || new Date().toISOString().split('T')[0],
      });
    }
  }

  return rpcCall('post_expense', idempotencyKey('ep'), {
    business_id: businessId,
    expense_id: expenseId,
    row_version: 1,
    payments,
  }, token);
}

// ── SERVICE TRANSACTION BRIDGE ──────────────────────────────────────────────

/**
 * Persist a service transaction to the relational DB.
 */
export async function persistServiceTransaction(businessId, txnData, token, paymentAccounts) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');

  const txn = {
    service_id: null,
    service_name: txnData.serviceName || (txnData.items?.[0]?.serviceName) || '',
    customer_id: null,
    customer_name: txnData.customerName || null,
    txn_date: txnData.date || new Date().toISOString().split('T')[0],
    total_revenue: parseFloat(txnData.totalRevenue || 0),
    total_cost: parseFloat(txnData.totalCost || 0),
    profit: parseFloat(txnData.profit || 0),
    is_paid: !!txnData.isPaid,
  };

  // Step 1: create draft
  const draftResult = await rpcCall('create_service_txn_draft', idempotencyKey('std'), {
    business_id: businessId,
    txn,
  }, token);

  const txnId = draftResult?.data?.service_txn_id;
  if (!txnId) throw new Error('No service_txn_id returned from draft');

  // Step 2: post with payments (row_version=1)
  const payments = buildPayments(txnData.payments, txnData.date, paymentAccounts);

  return rpcCall('post_service_txn', idempotencyKey('stp'), {
    business_id: businessId,
    service_txn_id: txnId,
    row_version: 1,
    payments,
  }, token);
}

// ── SALES RETURN BRIDGE ─────────────────────────────────────────────────────

/**
 * Build inventory movements for a sales return.
 * Sales returns ADD stock back: qty_delta is positive, movement_value is positive.
 * unit_cost is the original sale cost snapshot (for COGS reversal accuracy).
 * Only items with a valid product UUID produce movements.
 */
export function buildSalesReturnMovements(items, returnDate) {
  return (items || [])
    .filter((it) => {
      if ((it.type || 'product') === 'service') return false;
      if (parseFloat(it.qty || it.quantity || 0) <= 0) return false;
      return it.productId && isUUID(it.productId);
    })
    .map((it) => {
      const qty = Math.abs(parseFloat(it.qty || it.quantity || 0));
      const unitCost = parseFloat(it.unitCost || it.cost || 0);
      return {
        product_id: it.productId,
        qty_delta: qty,                             // positive: stock returns to inventory
        unit_cost: unitCost,
        movement_value: qty * unitCost,             // positive (same sign as qty_delta)
        accounting_date: returnDate || new Date().toISOString().split('T')[0],
      };
    });
}

/**
 * Persist a sales return to the relational DB.
 * Requires returnData.originalSaleRid (parent sale relational UUID) and
 * item-level productId for full relational persistence. If the parent sale
 * has no relational UUID, the RPC will fail and the error is propagated
 * (not swallowed) so the caller can log diagnostic information.
 */
export async function persistSalesReturn(businessId, returnData, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');

  // Resolve the parent sale's relational UUID — null means the parent predates
  // the dual-write and the relational return cannot be created (NOT NULL FK).
  const originalSaleRid = returnData.originalSaleRid || null;
  if (!originalSaleRid) {
    throw new Error('Cannot persist sales return: parent sale has no relational UUID (_rid). Legacy-only record.');
  }

  const ret = {
    original_sale_id: originalSaleRid,
    return_date: returnData.date || new Date().toISOString().split('T')[0],
    refund_amount: parseFloat(returnData.refundAmt || returnData.totalRefund || 0),
    restock: true,
    reason: returnData.reason || '',
  };

  const items = (returnData.items || []).map((it) => ({
    product_id: (it.productId && isUUID(it.productId)) ? it.productId : null,
    product_name: it.productName || '',
    quantity: parseFloat(it.qty || it.quantity || 0),
    unit_cost: parseFloat(it.unitCost || it.cost || 0),
  }));

  // Validate: every non-service item must have a product UUID for the NOT NULL FK
  const missingProductId = items.some((it) => !it.product_id);
  if (missingProductId) {
    throw new Error('Cannot persist sales return: one or more items missing product UUID. Legacy-only record.');
  }

  const movements = buildSalesReturnMovements(returnData.items, returnData.date);

  // Step 1: create draft
  const draftResult = await rpcCall('create_sales_return_draft', idempotencyKey('srd'), {
    business_id: businessId,
    ret,
    items,
  }, token);

  const returnId = draftResult?.data?.sales_return_id;
  if (!returnId) throw new Error('No sales_return_id returned from draft');

  // Step 2: post with actual inventory movements
  return rpcCall('post_sales_return', idempotencyKey('srp'), {
    business_id: businessId,
    sales_return_id: returnId,
    row_version: 1,
    movements,
  }, token);
}

// ── PURCHASE RETURN BRIDGE ──────────────────────────────────────────────────

/**
 * Build inventory movements for a purchase return.
 * Purchase returns REMOVE stock (returned to supplier): qty_delta is negative,
 * movement_value is negative. Only items with a valid product UUID produce
 * movements.
 */
export function buildPurchaseReturnMovements(items, returnDate) {
  return (items || [])
    .filter((it) => {
      if (parseFloat(it.qty || it.quantity || 0) <= 0) return false;
      return it.productId && isUUID(it.productId);
    })
    .map((it) => {
      const qty = Math.abs(parseFloat(it.qty || it.quantity || 0));
      const unitCost = parseFloat(it.unitCost || it.unitPrice || it.cost || 0);
      return {
        product_id: it.productId,
        qty_delta: -qty,                            // negative: stock leaves inventory
        unit_cost: unitCost,
        movement_value: -(qty * unitCost),          // negative (same sign as qty_delta)
        accounting_date: returnDate || new Date().toISOString().split('T')[0],
      };
    });
}

/**
 * Persist a purchase return to the relational DB.
 * Requires returnData.originalPurchaseRid (parent purchase relational UUID)
 * and item-level productId for full relational persistence. If the parent
 * purchase has no relational UUID, the error is propagated (not swallowed)
 * so the caller can log diagnostic information.
 */
export async function persistPurchaseReturn(businessId, returnData, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');

  // Resolve the parent purchase's relational UUID — null means the parent
  // predates the dual-write and the relational return cannot be created.
  const originalPurchaseRid = returnData.originalPurchaseRid || null;
  if (!originalPurchaseRid) {
    throw new Error('Cannot persist purchase return: parent purchase has no relational UUID (_rid). Legacy-only record.');
  }

  const ret = {
    original_purchase_id: originalPurchaseRid,
    return_date: returnData.date || new Date().toISOString().split('T')[0],
    refund_amount: parseFloat(returnData.refundAmt || returnData.totalRefund || 0),
    restock: returnData.restock !== false,
    reason: returnData.reason || '',
  };

  const items = (returnData.items || []).map((it) => ({
    product_id: (it.productId && isUUID(it.productId)) ? it.productId : null,
    product_name: it.productName || '',
    quantity: parseFloat(it.qty || it.quantity || 0),
    unit_cost: parseFloat(it.unitCost || it.unitPrice || it.cost || 0),
  }));

  // Validate: every item must have a product UUID for the NOT NULL FK
  const missingProductId = items.some((it) => !it.product_id);
  if (missingProductId) {
    throw new Error('Cannot persist purchase return: one or more items missing product UUID. Legacy-only record.');
  }

  const movements = buildPurchaseReturnMovements(returnData.items, returnData.date);

  const draftResult = await rpcCall('create_purchase_return_draft', idempotencyKey('prd'), {
    business_id: businessId,
    ret,
    items,
  }, token);

  const returnId = draftResult?.data?.purchase_return_id;
  if (!returnId) throw new Error('No purchase_return_id returned from draft');

  return rpcCall('post_purchase_return', idempotencyKey('prp'), {
    business_id: businessId,
    purchase_return_id: returnId,
    row_version: 1,
    movements,
  }, token);
}

// ── CASH/WALLET BRIDGE ──────────────────────────────────────────────────────

export async function persistCashAdjustment(businessId, adjustmentData, token, paymentAccounts) {
  if (!businessId || !token) return null;

  // Resolve the target wallet's relational UUID
  const accountKey = adjustmentData.accountKey || adjustmentData.account || 'cash';
  const mapping = resolvePaymentMapping({ type: accountKey.split('|')[0], accountKey }, paymentAccounts);

  // cash_adjustments requires payment_account_id NOT NULL — skip if unresolvable
  if (!mapping.payment_account_id) {
    console.warn('[MIGRATION-ONLY] cash adjustment skipped: no relational payment_account_id for', accountKey);
    return null;
  }

  const { rpc: rpcCall } = await import('./rpc');
  return rpcCall('create_cash_adjustment', idempotencyKey('ca'), {
    business_id: businessId,
    payment_account_id: mapping.payment_account_id,
    direction: adjustmentData.type === 'credit' ? 'in' : 'out',
    amount: parseFloat(adjustmentData.amount || 0),
    reason: adjustmentData.description || adjustmentData.reason || '',
    adjustment_date: adjustmentData.date || new Date().toISOString().split('T')[0],
  }, token);
}

// ── MASTER DATA BRIDGE ──────────────────────────────────────────────────────

export async function persistProduct(businessId, product, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const productPayload = {
    product_name: product.productName || product.name || '',
    category: product.category || null,
    unit: product.unit || 'piece',
    cost_price: parseFloat(product.costPrice || 0),
    selling_price: parseFloat(product.sellingPrice || 0),
    opening_stock_qty: parseFloat(product.openingStockQty || 0),
    opening_stock_value: parseFloat(product.openingStockValue || 0),
  };
  const rid = resolveRid(product);
  if (rid) productPayload.id = rid;
  const payload = { business_id: businessId, product: productPayload };
  const rv = product._rv ?? product.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_product', idempotencyKey('prod'), payload, token);
}

export async function persistCustomer(businessId, customer, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const custPayload = {
    customer_name: customer.name || customer.customerName || '',
    customer_phone: customer.phone || '',
    customer_email: customer.email || '',
    address: customer.address || '',
  };
  const rid = resolveRid(customer);
  if (rid) custPayload.id = rid;
  const payload = { business_id: businessId, customer: custPayload };
  const rv = customer._rv ?? customer.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_customer', idempotencyKey('cust'), payload, token);
}

export async function persistSupplier(businessId, supplier, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const suppPayload = {
    supplier_name: supplier.name || supplier.supplierName || '',
    phone: supplier.phone || '',
    email: supplier.email || '',
    address: supplier.address || '',
  };
  const rid = resolveRid(supplier);
  if (rid) suppPayload.id = rid;
  const payload = { business_id: businessId, supplier: suppPayload };
  const rv = supplier._rv ?? supplier.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_supplier', idempotencyKey('supp'), payload, token);
}

export async function persistExpenseCategory(businessId, category, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const catPayload = { name: category.name || '' };
  const rid = resolveRid(category);
  if (rid) catPayload.id = rid;
  const payload = { business_id: businessId, category: catPayload };
  const rv = category._rv ?? category.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_expense_category', idempotencyKey('ecat'), payload, token);
}

// ── SERVICE CATALOGUE BRIDGE ───────────────────────────────────────────────

/** MIGRATION-ONLY: persist service catalogue entry to relational DB */
export async function persistService(businessId, service, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const svcPayload = {
    name: service.name || '',
    default_price: parseFloat(service.defaultPrice || 0),
    default_cost: parseFloat(service.defaultCost || 0),
  };
  const rid = resolveRid(service);
  if (rid) svcPayload.id = rid;
  const payload = { business_id: businessId, service: svcPayload };
  const rv = service._rv ?? service.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_service', idempotencyKey('svc'), payload, token);
}

// ── PAYMENT ACCOUNT BRIDGE ─────────────────────────────────────────────────

/** MIGRATION-ONLY: persist payment account to relational DB */
export async function persistPaymentAccount(businessId, account, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');
  const acctPayload = {
    type: account.type || 'cash',
    bank_name: account.bankName || null,
    network_name: account.networkName || null,
    account_name: account.accountName || '',
    account_number: account.accountNumber || null,
    phone_number: account.phoneNumber || null,
    opening_balance: parseFloat(account.openingBalance || 0),
  };
  const rid = resolveRid(account);
  if (rid) acctPayload.id = rid;
  const payload = { business_id: businessId, account: acctPayload };
  const rv = account._rv ?? account.rowVersion;
  if (rv != null) payload.row_version = rv;
  return rpcCall('upsert_payment_account', idempotencyKey('acct'), payload, token);
}

// ── INVOICE BRIDGE ─────────────────────────────────────────────────────────

/** MIGRATION-ONLY: persist manual invoice (draft) to relational DB */
export async function persistInvoice(businessId, invoice, token) {
  if (!businessId || !token) return null;

  const { rpc: rpcCall } = await import('./rpc');

  const invoicePayload = {
    customer_id: null,
    customer_name: invoice.customerName || null,
    invoice_date: invoice.date || new Date().toISOString().split('T')[0],
    total_amount: parseFloat(invoice.totalRevenue || 0),
    notes: invoice.notes || '',
  };

  const items = (invoice.items || []).map((it) => ({
    product_id: null,
    product_name: it.productName || '',
    quantity: parseFloat(it.quantity || 0),
    unit_price: parseFloat(it.sellingPrice || it.unitPrice || 0),
  }));

  return rpcCall('create_invoice_draft', idempotencyKey('inv'), {
    business_id: businessId,
    invoice: invoicePayload,
    items,
  }, token);
}

// ── TRANSACTION PAYMENT RECORDING ──────────────────────────────────────────
// These bridge the debt/loan payment handlers to the relational payment recording
// commands. Only called when the sale/purchase has a relational UUID (_rid or UUID id).

export async function bridgeRecordSalePayment(businessId, saleRid, payment, token, paymentAccounts) {
  if (!businessId || !token || !saleRid) return null;
  const mapped = resolvePaymentMapping(payment, paymentAccounts);
  const { rpc: rpcCall } = await import('./rpc');
  return rpcCall('record_sale_payment', idempotencyKey('spay'), {
    business_id: businessId,
    sale_id: saleRid,
    payment_account_id: mapped.payment_account_id,
    wallet_type: mapped.wallet_type,
    amount: parseFloat(payment.amount || 0),
    payment_method: payment.type || 'cash',
    payment_date: payment.date || new Date().toISOString().split('T')[0],
  }, token);
}

export async function bridgeRecordPurchasePayment(businessId, purchaseRid, payment, token, paymentAccounts) {
  if (!businessId || !token || !purchaseRid) return null;
  const mapped = resolvePaymentMapping(payment, paymentAccounts);
  const { rpc: rpcCall } = await import('./rpc');
  return rpcCall('record_purchase_payment', idempotencyKey('ppay'), {
    business_id: businessId,
    purchase_id: purchaseRid,
    payment_account_id: mapped.payment_account_id,
    wallet_type: mapped.wallet_type,
    amount: parseFloat(payment.amount || 0),
    payment_method: payment.type || 'cash',
    payment_date: payment.date || new Date().toISOString().split('T')[0],
  }, token);
}

export async function bridgeRecordInvoiceDeposit(businessId, invoiceRid, deposit, token, paymentAccounts) {
  if (!businessId || !token || !invoiceRid) return null;
  const mapped = resolvePaymentMapping(deposit, paymentAccounts);
  const { rpc: rpcCall } = await import('./rpc');
  return rpcCall('record_invoice_deposit', idempotencyKey('idep'), {
    business_id: businessId,
    invoice_id: invoiceRid,
    payment_account_id: mapped.payment_account_id,
    wallet_type: mapped.wallet_type,
    amount: parseFloat(deposit.amount || 0),
    payment_method: deposit.type || 'cash',
    payment_date: deposit.date || new Date().toISOString().split('T')[0],
  }, token);
}

/** Convert a fully-paid manual invoice to a sale (relational path). */
export async function bridgeConvertInvoiceToSale(businessId, invoiceRid, saleData, token, paymentAccounts) {
  if (!businessId || !token || !invoiceRid) return null;
  const { rpc: rpcCall } = await import('./rpc');
  const sale = buildSaleHeader(saleData);
  const items = buildSaleItems(saleData.items);
  const movements = buildSaleMovements(saleData.items, saleData.date);
  const payments = buildSalePayments(saleData.payments, saleData.date, paymentAccounts);
  return rpcCall('convert_invoice_to_sale', idempotencyKey('ic'), {
    business_id: businessId,
    invoice_id: invoiceRid,
    sale,
    items,
    movements,
    payments,
  }, token);
}

// ── UUID helper export (used by App.js to check if a record has a relational ID)
export { isUUID, resolveRid, resolvePaymentMapping };

// ── Shared payment builder ──────────────────────────────────────────────────
function buildPayments(payments, date, paymentAccounts) {
  const result = [];
  if (Array.isArray(payments) && payments.length > 0) {
    payments.forEach((p) => {
      if (p.amount && parseFloat(p.amount) > 0) {
        const mapped = resolvePaymentMapping(p, paymentAccounts);
        result.push({
          payment_account_id: mapped.payment_account_id,
          wallet_type: mapped.wallet_type,
          amount: parseFloat(p.amount),
          payment_method: p.type || 'cash',
          payment_date: date || new Date().toISOString().split('T')[0],
        });
      }
    });
  }
  return result;
}
